/**
 * HTTP client for the DragonIS CDR API.
 *
 * The module owns three concerns and nothing else:
 *
 *  1. `apiFetch<T>` — a single fetch wrapper that sets the auth/CSRF/JSON
 *     boilerplate every caller needs. Callers do NOT touch `window.fetch`
 *     directly; a test of "is the CSRF header attached" is a test of this
 *     function.
 *  2. `ApiError` — a typed error for any non-2xx response. The server always
 *     emits RFC 7807 `application/problem+json`; we decode it once here so
 *     consumers switch on `.type` / `.status` instead of string-matching.
 *  3. `setCsrfTokenGetter` — a tiny dependency-injection shim so the fetch
 *     function can read the current CSRF token without importing React.
 *     `AuthProvider` wires this up on mount; tests wire it up directly.
 *
 * Everything else (endpoint paths, zod parsing, query keys) belongs in the
 * thin wrappers under `./auth.ts`, `./runs.ts`, `./customers.ts`.
 *
 * Design notes worth defending:
 *
 *  - CSRF is attached on any method other than GET / HEAD. That matches the
 *    API's `requireCsrf` preHandler (see `apps/api/src/http/auth-middleware.ts`
 *    on the server side). If the token is null — e.g. a mutation raced the
 *    `GET /auth/me` bootstrap — we still send the request without the header
 *    and let the server emit a 401/403. Swallowing the call client-side would
 *    hide the real failure mode.
 *  - Redirects (302 from download endpoints) are NOT consumed here. Callers
 *    construct `<a href>` URLs directly; fetch-follows-redirect-into-R2 would
 *    either leak cookies to an unrelated origin or fail CORS. See
 *    `runs.ts::downloadUrl`.
 *  - The default Accept header is `application/json, application/problem+json`.
 *    Sending both lets the server negotiate cleanly whether a response is a
 *    success or an error without the client lying about what it accepts.
 */

const API_BASE = '/api/v1';

/**
 * Typed error for any non-2xx response. The fields map 1:1 to RFC 7807.
 *
 * `instance` is optional because some error responses (e.g. a network failure
 * caught before a response exists) have no URL context. `detail` defaults to
 * `title` when the server omits it, so consumers always have a string to show
 * the user — never `undefined`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly type: string;
  readonly title: string;
  readonly detail: string;
  readonly instance?: string;

  constructor(opts: {
    status: number;
    type: string;
    title: string;
    detail: string;
    instance?: string;
  }) {
    super(opts.detail);
    this.name = 'ApiError';
    this.status = opts.status;
    this.type = opts.type;
    this.title = opts.title;
    this.detail = opts.detail;
    if (opts.instance !== undefined) {
      this.instance = opts.instance;
    }
  }
}

// --- CSRF token getter shim -------------------------------------------------
//
// `apiFetch` cannot import React without coupling every consumer to the
// `AuthProvider` tree, and it cannot call `useAuth()` outside a component.
// The indirection here is deliberate: `AuthProvider` registers a getter on
// mount; callers outside React (tests, one-off scripts) can call
// `setCsrfTokenGetter` with whatever source of truth they have.
//
// A module-level closure is fine because there is exactly one CSRF token in
// the app at any time — session state is a tree-wide singleton.

type CsrfTokenGetter = () => string | null;

let csrfTokenGetter: CsrfTokenGetter = () => null;

export function setCsrfTokenGetter(getter: CsrfTokenGetter): void {
  csrfTokenGetter = getter;
}

/**
 * Test-only reset. Exported under a separate name to make it clear that
 * production code should never call it. Vitest test files use this in
 * `beforeEach` to guarantee isolation between tests.
 */
export function __resetCsrfTokenGetterForTests(): void {
  csrfTokenGetter = () => null;
}

// --- apiFetch ---------------------------------------------------------------

/**
 * Methods for which we never attach a CSRF header.
 *
 * The server's `requireCsrf` preHandler only runs on routes registered with
 * the `mutating` option; GET/HEAD routes never invoke it. Attaching the header
 * to a GET is harmless but noisy, and it leaks the token into logs/proxies
 * for no gain.
 */
const IDEMPOTENT_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD']);

function shouldAttachCsrf(method: string): boolean {
  return !IDEMPOTENT_METHODS.has(method.toUpperCase());
}

/**
 * Normalise the input URL so callers can pass either a full path
 * (`/api/v1/runs`) or a relative one (`runs`). Prefixing with the API base
 * when the caller gives us a leading slash that does not already start with
 * `/api/` is the common-case convenience; anything else goes through
 * unchanged so callers can hit `/healthz` or an absolute URL if they need to.
 */
function resolveUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith('/api/') || path === '/healthz') return path;
  if (path.startsWith('/')) return `${API_BASE}${path}`;
  return `${API_BASE}/${path}`;
}

/**
 * True if the request body is a plain JSON payload — not FormData, not a
 * Blob, not a ReadableStream. We only stamp `Content-Type: application/json`
 * in that case; multipart uploads MUST set their own boundary header, and
 * overriding it here would break the `/runs` upload route.
 */
function isJsonBody(body: BodyInit | null | undefined): boolean {
  if (body == null) return false;
  if (typeof body === 'string') return true;
  // FormData / Blob / URLSearchParams / ArrayBuffer / ReadableStream: leave
  // the Content-Type alone.
  return false;
}

export interface ApiFetchInit extends Omit<RequestInit, 'body'> {
  /**
   * If you pass a non-string body (FormData, Blob, etc.), DO NOT serialise it
   * yourself; hand it in raw and `apiFetch` will skip the JSON
   * Content-Type stamp. Strings are assumed to already be JSON-serialised by
   * the caller (so a caller that wants to `JSON.stringify` can do so exactly
   * once).
   */
  body?: BodyInit | null;
}

/**
 * Perform an authenticated fetch against the API.
 *
 * On success:
 *   - 204 No Content → resolves to `undefined as T` (callers typing the
 *     return as `void` will get a clean result).
 *   - Otherwise the body is parsed as JSON. The caller is responsible for
 *     zod-validating the shape — `apiFetch` does not know which schema
 *     applies.
 *
 * On failure:
 *   - If the response has `application/problem+json` and a JSON body,
 *     `ApiError` is thrown with the parsed fields.
 *   - If the response is non-2xx but opaque (no problem body), an `ApiError`
 *     is still thrown so callers never have to branch on `.ok` themselves.
 *   - Network failures propagate as the underlying `TypeError` so callers
 *     can distinguish "server rejected" from "no server reached".
 */
export async function apiFetch<T>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);

  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json, application/problem+json');
  }

  if (isJsonBody(init.body) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (shouldAttachCsrf(method)) {
    const token = csrfTokenGetter();
    if (token !== null && token.length > 0 && !headers.has('X-CSRF-Token')) {
      headers.set('X-CSRF-Token', token);
    }
  }

  const response = await fetch(resolveUrl(path), {
    ...init,
    method,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    throw await buildApiError(response);
  }

  if (response.status === 204) {
    // Explicit cast: the caller types the generic, we guarantee nothing else
    // sensible could come back for a 204.
    return undefined as T;
  }

  // We trust the server's Content-Type — if it sent 200 with an empty body
  // the JSON parse below will throw and the caller will see a clear error.
  return (await response.json()) as T;
}

/**
 * Parse a non-OK response into an `ApiError`.
 *
 * The server always emits problem+json for application-level errors (see
 * `apps/api/src/http/errors.ts::installErrorHandler`). We still guard against
 * the upstream-proxy case where the response is HTML or empty — a generic
 * fallback keeps the UI layer from having to handle `undefined` properties.
 */
async function buildApiError(response: Response): Promise<ApiError> {
  const contentType = response.headers.get('Content-Type') ?? '';
  const isProblem =
    contentType.includes('application/problem+json') || contentType.includes('application/json');

  if (isProblem) {
    try {
      const body = (await response.json()) as Partial<{
        type: string;
        title: string;
        status: number;
        detail: string;
        instance: string;
      }>;
      const opts: {
        status: number;
        type: string;
        title: string;
        detail: string;
        instance?: string;
      } = {
        status: typeof body.status === 'number' ? body.status : response.status,
        type: typeof body.type === 'string' ? body.type : 'about:blank',
        title: typeof body.title === 'string' ? body.title : response.statusText || 'Error',
        detail:
          typeof body.detail === 'string' && body.detail.length > 0
            ? body.detail
            : typeof body.title === 'string'
              ? body.title
              : response.statusText || 'Error',
      };
      if (typeof body.instance === 'string') {
        opts.instance = body.instance;
      }
      return new ApiError(opts);
    } catch {
      // Fall through to the opaque case — the server claimed JSON but gave us
      // something we could not parse. Better to surface a typed error than a
      // raw SyntaxError.
    }
  }

  return new ApiError({
    status: response.status,
    type: 'about:blank',
    title: response.statusText || 'Error',
    detail: response.statusText || `Request failed with status ${response.status}`,
  });
}
